import { cloneElement, Children, Component } from 'react';

import classNames from 'classnames';

import { Card as MuiCard, Collapse, IconButton } from '@mui/material';
import Divider from '@classify/react-core-components/Divider';

import withStyles from '@mui/styles/withStyles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { collapsible } from '../../../collapse';
import { Menu } from '../../../navigation';

import CardActions from './CardActions';
import CardActionButton from './CardActionButton';
import CardActionLabel from './CardActionLabel';
import CardContent from './CardContent';
import CardImage from './CardImage';
import CardTitle from './CardTitle';

export const styles = /* istanbul ignore next */ (theme) => ({
  actions: {},
  expand: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    marginLeft: ['auto', '!important'],
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  subContent: {
    paddingTop: 0,
  },
  title: {
    '& + $content': {
      paddingTop: 0,
    },
  },
  divider: {
    margin: [[theme.spacing(2), 0]],
  },
  content: {},
  root: {},
});

export class Card extends Component {
  static defaultProps = {
    actions: [],
    children: null,
    className: '',
    classes: {},
    collapsibleContent: null,
    collapsibleLabels: {},
    decoration: null,
    headerMenu: [],
    image: null,
    isCollapsed: false,
    isReadMoreType: false,
    /* istanbul ignore next */
    onToggleCollapse() {},
    subtitle: null,
    title: null,
    titleProps: {},
    ariaLabel: 'Show more actions',
    ariaLabelCollapse: 'Show Less information',
  };

  state = {
    isHeaderMenuOpen: false,
  };

  handleHeaderMenuClose = () => this.setState(() => ({ isHeaderMenuOpen: false }));

  handleHeaderMenuOpen = () => this.setState(() => ({ isHeaderMenuOpen: true }));

  render() {
    const {
      actions,
      children,
      className,
      classes,
      collapsibleContent,
      collapsibleLabels,
      decoration,
      headerMenu,
      image,
      isCollapsed,
      isReadMoreType,
      onToggleCollapse,
      subtitle,
      title,
      titleProps,
      ariaLabel,
      ariaLabelCollapse,
      ...props
    } = this.props;
    const { isHeaderMenuOpen } = this.state;

    let headerMenuElement = null;

    if (title && headerMenu.length) {
      const menuItems = Children.map(headerMenu, (menuItem) => {
        const originalOnClick = menuItem.props.onClick || null;

        return cloneElement(menuItem, {
          onClick: /* istanbul ignore next */ () => {
            this.handleHeaderMenuClose();

            if (originalOnClick) {
              originalOnClick();
            }
          },
        });
      });

      headerMenuElement = (
        <Menu
          anchorElement={
            <IconButton size="large" aria-label={ariaLabel}>
              <MoreHorizIcon />
            </IconButton>
          }
          isOpen={isHeaderMenuOpen}
          onClose={this.handleHeaderMenuClose}
          onOpen={this.handleHeaderMenuOpen}
        >
          {menuItems}
        </Menu>
      );
    }

    const cardActions = [...actions];
    const cardActionsReadMoreType = [];

    if (collapsibleContent && !isReadMoreType) {
      cardActions.push(
        <CardActionButton
          key="expand"
          isIconOnly
          className={classNames(classes.expand, {
            [classes.expandOpen]: isCollapsed,
          })}
          onClick={onToggleCollapse}
          aria-expanded={isCollapsed}
          color="secondary"
          aria-label={isCollapsed ? ariaLabel : ariaLabelCollapse}
        >
          <ExpandMoreIcon />
        </CardActionButton>
      );
    } else if (collapsibleContent && isReadMoreType) {
      cardActionsReadMoreType.push(
        <CardActionButton
          key="expand"
          onClick={onToggleCollapse}
          aria-expanded={isCollapsed}
          color="primary"
        >
          {isCollapsed ? collapsibleLabels.close : collapsibleLabels.open}
        </CardActionButton>
      );
    }

    return (
      <MuiCard {...props} className={`${className} ${classes.root}`}>
        {image && <CardImage src={image} />}

        {title && (
          <CardTitle
            action={headerMenuElement}
            className={classes.title}
            decoration={decoration}
            subtitle={subtitle}
            {...titleProps}
          >
            {title}
          </CardTitle>
        )}

        {children && (
          <CardContent className={classes.content} decoration={decoration}>
            {children}
          </CardContent>
        )}

        {!!cardActions.length && !isReadMoreType && (
          <CardActions className={classes.actions}>{cardActions}</CardActions>
        )}

        {collapsibleContent && (
          <Collapse in={isCollapsed} timeout="auto">
            <CardContent className={classes.subContent}>{collapsibleContent}</CardContent>
          </Collapse>
        )}

        {isReadMoreType && collapsibleLabels && (
          <Divider className={classes.divider} key="divider" />
        )}

        {isReadMoreType && collapsibleLabels && (
          <CardActions className={classes.actions}>{cardActionsReadMoreType}</CardActions>
        )}
      </MuiCard>
    );
  }
}

const CollapsibleCard = withStyles(styles)(collapsible(Card));

Card.CardActions = CardActions;
Card.ActionButton = CardActionButton;
Card.ActionLabel = CardActionLabel;
CollapsibleCard.CardActions = CardActions;
CollapsibleCard.ActionButton = CardActionButton;
CollapsibleCard.ActionLabel = CardActionLabel;

export default CollapsibleCard;
